import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { Button } from 'components/Button'
import { Input } from 'components/Input'
import { InputDate } from 'components/InputDate'
import { InputSelect } from 'components/InputSelect'

import {
  chargeDataLogsSelectors,
  chargeDataLogsActions
} from 'store/ChargeDataLogs'

import { submitOnEnter } from 'utils/submitOnEnter'

import * as S from './styled'

const INITIAL_FILTER_STATE = {
  startDate: null,
  endDate: null,
  userEmail: '',
  fileName: '',
  status: ''
}

const STATUS_OPTIONS = [
  { value: '', title: 'all' },
  { value: 'success', title: 'success' },
  { value: 'error', title: 'error' }
]

export function ChargeDataFilter() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { logs } = useSelector(chargeDataLogsSelectors.all)

  const [filters, setFilters] = useState(INITIAL_FILTER_STATE)
  const [errors, setErrors] = useState(INITIAL_FILTER_STATE)

  function handleInput(name: string) {
    return function _handle(val: string | Date) {
      setErrors({ ...errors, [name]: '' })
      setFilters({ ...filters, [name]: val })
    }
  }

  function handleSelect(name: string) {
    return function _handle(option: any) {
      setErrors({ ...errors, [name]: '' })
      setFilters({ ...filters, [name]: option.value })
    }
  }

  function validateFilters() {
    const newErrors = { ...INITIAL_FILTER_STATE }
    let hasErrors = false

    // Validação de data inicial e final
    if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate)
      const endDate = new Date(filters.endDate)

      if (startDate > endDate) {
        newErrors.endDate = 'Data final deve ser maior que a data inicial'
        hasErrors = true
      }
    }

    setErrors(newErrors)
    return !hasErrors
  }

  function submitFilter() {
    if (!validateFilters()) return

    const filterParams: any = {
      page: 0,
      limit: logs.pagination?.limit || 10
    }

    // Adicionar filtros apenas se tiverem valor
    if (filters.startDate) {
      filterParams.startDate = filters.startDate
    }
    if (filters.endDate) {
      filterParams.endDate = filters.endDate
    }
    if (filters.userEmail.trim()) {
      filterParams.userEmail = filters.userEmail.trim()
    }
    if (filters.fileName.trim()) {
      filterParams.fileName = filters.fileName.trim()
    }
    if (filters.status) {
      filterParams.status = filters.status
    }

    dispatch(chargeDataLogsActions.fetchAll(filterParams))
  }

  function clearFilters() {
    setFilters(INITIAL_FILTER_STATE)
    setErrors(INITIAL_FILTER_STATE)

    const params = {
      page: 0,
      limit: logs.pagination?.limit || 10
    }

    dispatch(chargeDataLogsActions.fetchAll(params))
  }

  return (
    <S.Wrapper>
      <S.GridHeader>
        <S.GridTitle item container xs={12}>
          <S.BoxTitle>{t('chargeData:filters')}</S.BoxTitle>
          <S.BoxLine />
        </S.GridTitle>
      </S.GridHeader>

      <S.GridFilter container spacing={2}>
        <S.GridInput item xs={12} sm={6} md={2}>
          <InputDate
            fullWidth
            label={t('chargeData:startDate')}
            value={filters.startDate}
            error={Boolean(errors.startDate)}
            helperText={errors.startDate}
            onInput={handleInput('startDate')}
          />
        </S.GridInput>

        <S.GridInput item xs={12} sm={6} md={2}>
          <InputDate
            fullWidth
            label={t('chargeData:endDate')}
            value={filters.endDate}
            error={Boolean(errors.endDate)}
            helperText={errors.endDate}
            onInput={handleInput('endDate')}
          />
        </S.GridInput>

        <S.GridInput item xs={12} sm={6} md={2}>
          <Input
            fullWidth
            label={t('chargeData:userEmail')}
            value={filters.userEmail}
            error={Boolean(errors.userEmail)}
            helperText={errors.userEmail}
            onKeyPress={submitOnEnter(submitFilter)}
            onInput={value => {
              setErrors({ ...errors, userEmail: '' })
              setFilters({ ...filters, userEmail: value })
            }}
            placeholder={t('chargeData:userEmailPlaceholder')}
          />
        </S.GridInput>

        <S.GridInput item xs={12} sm={6} md={2}>
          <Input
            fullWidth
            label={t('chargeData:fileName')}
            value={filters.fileName}
            error={Boolean(errors.fileName)}
            helperText={errors.fileName}
            onKeyPress={submitOnEnter(submitFilter)}
            onInput={value => {
              setErrors({ ...errors, fileName: '' })
              setFilters({ ...filters, fileName: value })
            }}
            placeholder={t('chargeData:fileNamePlaceholder')}
          />
        </S.GridInput>

        <S.GridInput item xs={12} sm={6} md={2}>
          <InputSelect
            fullWidth
            label={t('chargeData:status')}
            optionLabel="title"
            optionValue="value"
            options={STATUS_OPTIONS.map(option => ({
              title: t(`chargeData:${option.title}`),
              value: option.value
            }))}
            value={filters.status}
            onSelected={handleSelect('status')}
            error={Boolean(errors.status)}
            helperText={errors.status}
          />
        </S.GridInput>

        <S.GridButtons item xs={12} sm={12} md={2}>
          <S.ButtonContainer>
            <Button variant="default" onClick={clearFilters} size="medium">
              {t('chargeData:clear')}
            </Button>
            <Button variant="primary" onClick={submitFilter} size="medium">
              {t('chargeData:apply')}
            </Button>
          </S.ButtonContainer>
        </S.GridButtons>
      </S.GridFilter>
    </S.Wrapper>
  )
}

export default ChargeDataFilter
